/**
 * Hook to fetch progress log Communications for a Task.
 * @agentic connections
 */

import { useCallback, useEffect, useReducer, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { Communication } from "@medplum/fhirtypes";
import { getProgressLogs } from "@benchmark/core/services";
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Return type for useActivityProgressLogs.
 * @agentic connections
 */
type UseActivityProgressLogsResult = {
  logs: Communication[];
  count: number;
  loading: boolean;
};

type State = {
  logs: Communication[];
  loading: boolean;
};

type Action =
  | { type: "start" }
  | { type: "success"; logs: Communication[] }
  | { type: "error" }
  | { type: "reset" };

const reducer = (_state: State, action: Action): State => {
  switch (action.type) {
    case "start":
      return { logs: [], loading: true };
    case "success":
      return { logs: action.logs, loading: false };
    case "error":
      return { logs: [], loading: false };
    case "reset":
      return { logs: [], loading: false };
  }
};

/**
 * Fetches progress log Communications linked to a Task via basedOn.
 * @agentic connections
 */
export const useActivityProgressLogs = (
  taskId: string | undefined,
): UseActivityProgressLogsResult => {
  const medplum = useMedplum();
  const [state, dispatch] = useReducer(reducer, { logs: [], loading: false });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!taskId) {
      dispatch({ type: "reset" });
      return;
    }

    let cancelled = false;
    dispatch({ type: "start" });

    getProgressLogs(medplum, taskId)
      .then((result) => {
        if (!cancelled) {
          dispatch({ type: "success", logs: result });
        }
      })
      .catch(() => {
        if (!cancelled) {
          dispatch({ type: "error" });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, taskId, reloadKey]);

  // Auto-refetch when a progress-log Communication for this Task changes.
  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);
  useEventDrivenRefetch(
    [
      DomainEventNames.CommunicationCreated,
      DomainEventNames.CommunicationUpdated,
    ],
    {
      refetch: triggerReload,
      enabled: !!taskId,
      filter: (payload) =>
        (
          payload as { communication?: Communication }
        ).communication?.basedOn?.some(
          (ref) => ref.reference === `Task/${taskId}`,
        ) ?? false,
    },
  );

  return { logs: state.logs, count: state.logs.length, loading: state.loading };
};
